import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

export type Language = 'en' | 'de' | 'es' | 'fr' | 'it' | 'pt' | 'nl' | 'ja' | 'ko' | 'ar' | 'zh';

interface LanguageContextType {
  currentLanguage: Language;
  changeLanguage: (lang: Language) => Promise<void>;
  isLoading: boolean;
  languages: { code: Language; name: string; flag: string; nativeName: string }[];
}

const languages = [
  { code: 'en' as Language, name: 'English', flag: '🇺🇸', nativeName: 'English' },
  { code: 'de' as Language, name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'es' as Language, name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'fr' as Language, name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'it' as Language, name: 'Italian', flag: '🇮🇹', nativeName: 'Italiano' },
  { code: 'pt' as Language, name: 'Portuguese', flag: '🇧🇷', nativeName: 'Português' },
  { code: 'nl' as Language, name: 'Dutch', flag: '🇳🇱', nativeName: 'Nederlands' },
  { code: 'ja' as Language, name: 'Japanese', flag: '🇯🇵', nativeName: '日本語' },
  { code: 'ko' as Language, name: 'Korean', flag: '🇰🇷', nativeName: '한국어' },
  { code: 'ar' as Language, name: 'Arabic', flag: '🇸🇦', nativeName: 'العربية' },
  { code: 'zh' as Language, name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
];

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n: i18nInstance } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const currentLanguage = (i18nInstance.language || 'en').split('-')[0] as Language;

  const changeLanguage = useCallback(async (lang: Language) => {
    if (lang === currentLanguage) return;
    
    setIsLoading(true);
    try {
      await i18nInstance.changeLanguage(lang);
      localStorage.setItem('noox-language', lang);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentLanguage, i18nInstance]);

  // Sync language from localStorage on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('noox-language') as Language;
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ 
      currentLanguage, 
      changeLanguage, 
      isLoading,
      languages 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { languages };
